import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { adminAPI } from '../../../services/api';
import {
  Loader2, Search, LayoutGrid, ChevronRight,
  CheckCircle2, XCircle, Save, ArrowLeft, Users,
} from 'lucide-react';

const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const TAB_ICONS = {
  profile:       '👤',
  wallet:        '💳',
  transactions:  '↔️',
  settlements:   '📊',
  catalogue:     '📦',
  payment_links: '🔗',
  shopify:       '🛍️',
  integration:   '⚙️',
  wordpress:     '🌐',
  support:       '🎧',
};

const AdminMerchantTabs = () => {
  const [merchants, setMerchants]       = useState([]);
  const [loadingList, setLoadingList]   = useState(true);
  const [search, setSearch]             = useState('');
  const [selected, setSelected]         = useState(null);
  const [tabs, setTabs]                 = useState([]);
  const [loadingTabs, setLoadingTabs]   = useState(false);
  const [saving, setSaving]             = useState(false);

  const loadMerchants = useCallback(async (q = '') => {
    setLoadingList(true);
    try {
      const res = await adminAPI.merchantTabs.list(q);
      setMerchants(res.data.data || []);
    } catch {
      toast.error('Failed to load merchants');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadMerchants(); }, [loadMerchants]);

  useEffect(() => {
    const t = setTimeout(() => loadMerchants(search), 350);
    return () => clearTimeout(t);
  }, [search, loadMerchants]);

  const selectMerchant = async (merchant) => {
    setSelected(merchant);
    setLoadingTabs(true);
    try {
      const res = await adminAPI.merchantTabs.getPermissions(merchant.id);
      setTabs(res.data.tabs || []);
    } catch {
      toast.error('Failed to load tab permissions');
    } finally {
      setLoadingTabs(false);
    }
  };

  const toggleTab = (key) => {
    setTabs(prev => prev.map(t => t.key === key ? { ...t, enabled: !t.enabled } : t));
  };

  const enableAll = () => setTabs(prev => prev.map(t => ({ ...t, enabled: true })));
  const disableAll = () => setTabs(prev => prev.map(t => ({ ...t, enabled: false })));

  const handleSave = async () => {
    setSaving(true);
    try {
      const disabledTabs = tabs.filter(t => !t.enabled).map(t => t.key);
      await adminAPI.merchantTabs.savePermissions(selected.id, { disabled_tabs: disabledTabs });
      toast.success('Tab permissions saved!');
      loadMerchants(search);
    } catch {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const thSt = {
    padding: '10px 14px', fontSize: 11, fontWeight: 700,
    color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px',
    textAlign: 'left', borderBottom: `1px solid ${C.border}`, background: C.bg,
  };

  return (
    <div style={{ padding: '24px 20px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
          <LayoutGrid size={20} color={C.primary} />
          Manage Merchant Tabs
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: C.body }}>
          Control which tabs each merchant can see in their portal
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '340px 1fr' : '1fr', gap: 16 }}>

        {/* LEFT — Merchant List */}
        <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, overflow: 'hidden' }}>

          {/* Search */}
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
              <input
                type="text"
                placeholder="Search merchant..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%', padding: '8px 12px 8px 32px', fontSize: 13,
                  background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: C.radiusSm, outline: 'none', boxSizing: 'border-box', color: C.ink,
                }}
              />
            </div>
          </div>

          {/* List */}
          <div style={{ maxHeight: 520, overflowY: 'auto' }}>
            {loadingList ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                <Loader2 size={20} color={C.muted} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : merchants.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: C.muted, fontSize: 13 }}>
                <Users size={28} style={{ marginBottom: 8, opacity: .5 }} />
                <p style={{ margin: 0 }}>No merchants found</p>
              </div>
            ) : merchants.map(m => {
              const isActive = selected?.id === m.id;
              const allEnabled = m.disabled_count === 0;
              return (
                <div
                  key={m.id}
                  onClick={() => selectMerchant(m)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${C.borderSoft}`,
                    background: isActive ? C.primarySoft : 'transparent',
                    borderLeft: isActive ? `3px solid ${C.primary}` : '3px solid transparent',
                    transition: 'background .15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: isActive ? C.primary : C.ink }}>
                      {m.username}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: C.muted }}>{m.email} · {m.mid}</p>
                    <p style={{ margin: '3px 0 0', fontSize: 11, color: allEnabled ? C.success : C.danger }}>
                      {allEnabled
                        ? 'All tabs enabled'
                        : `${m.disabled_count} tab${m.disabled_count > 1 ? 's' : ''} disabled`}
                    </p>
                  </div>
                  <ChevronRight size={14} color={C.muted} />
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — Tab Permissions */}
        {selected && (
          <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={() => setSelected(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}
                >
                  <ArrowLeft size={16} />
                </button>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.ink }}>{selected.username}</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{selected.mid} · {selected.email}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={enableAll} style={{
                  padding: '6px 12px', fontSize: 12, borderRadius: C.radiusSm,
                  border: `1px solid ${C.border}`, background: C.bg, color: C.success, cursor: 'pointer',
                }}>
                  Enable All
                </button>
                <button onClick={disableAll} style={{
                  padding: '6px 12px', fontSize: 12, borderRadius: C.radiusSm,
                  border: `1px solid ${C.border}`, background: C.bg, color: C.danger, cursor: 'pointer',
                }}>
                  Disable All
                </button>
                <button onClick={handleSave} disabled={saving} style={{
                  padding: '7px 16px', fontSize: 12, fontWeight: 600, borderRadius: C.radiusSm,
                  border: 'none', background: saving ? C.muted : C.primary, color: '#fff',
                  cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  {saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={12} />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            {/* Note */}
            <div style={{ padding: '10px 20px', background: '#FFFBEB', borderBottom: `1px solid #FDE68A`, fontSize: 12, color: '#92400E' }}>
              Dashboard tab is always visible and cannot be disabled.
            </div>

            {/* Tab Toggles */}
            {loadingTabs ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Loader2 size={22} color={C.muted} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <div style={{ padding: '8px 0' }}>
                {tabs.map(tab => (
                  <div
                    key={tab.key}
                    onClick={() => toggleTab(tab.key)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '13px 20px', cursor: 'pointer', borderBottom: `1px solid ${C.borderSoft}`,
                      background: tab.enabled ? 'transparent' : '#FEF2F2',
                      transition: 'background .15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 18 }}>{TAB_ICONS[tab.key] ?? '📄'}</span>
                      <div>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: tab.enabled ? C.ink : C.muted }}>
                          {tab.label}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: tab.enabled ? C.success : C.danger }}>
                          {tab.enabled ? 'Visible in merchant portal' : 'Hidden from merchant portal'}
                        </p>
                      </div>
                    </div>

                    {/* Toggle switch */}
                    <div style={{
                      width: 44, height: 24, borderRadius: 14,
                      background: tab.enabled ? C.primary : '#CBD5E1',
                      position: 'relative', transition: 'background .2s', flexShrink: 0,
                    }}>
                      <div style={{
                        position: 'absolute', top: 3,
                        left: tab.enabled ? 23 : 3,
                        width: 18, height: 18, borderRadius: '50%',
                        background: '#fff', transition: 'left .2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Placeholder when nothing selected */}
        {!selected && (
          <div style={{
            background: C.surface, borderRadius: C.radius, border: `1px dashed ${C.border}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 48, color: C.muted, display: 'none',
          }} />
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminMerchantTabs;
